import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import useAxiosClient from "../../../axios-client";

/**
 * ProductFormationsPane — master–detail pane for product formations scoped by product type.
 *
 * Each formation belongs to a specific Product Type (e.g., Sofa Cover, Pillow Cover,
 * Chair Cover). Like [FabricPatternsPane], this pane scopes to one product type
 * at a time using chips under the header, and keeps a separate view for historical
 * legacy formations.
 */
export default function ProductFormationsPane({
  productTypes = [],
  productTypesLoading = false,
  search = "",
  onSearchChange,
  searchInputRef,
  productFormations = [],
  formationsByType = {},
  productFormationsLoading = false,
  fetchProductFormations,
  onTotalChange,
}) {
  const axiosClient = useAxiosClient();

  // Selected product type ID or 'legacy'
  const [selectedTypeId, setSelectedTypeId] = useState(null);

  // Write strip state: { mode: 'create'|'rename'|'delete', id?, value? }
  const [write, setWrite] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const writeInputRef = useRef(null);

  const closeWrite = useCallback(() => {
    setWrite(null);
    setError(null);
    setSaving(false);
  }, []);

  // Default selection: open on the first product type or busiest
  useEffect(() => {
    if (selectedTypeId !== null || productTypes.length === 0) return;
    const busiest = productTypes.reduce((best, pt) => {
      const bestCount = (formationsByType[best.id] || []).length;
      const curCount = (formationsByType[pt.id] || []).length;
      return curCount > bestCount ? pt : best;
    }, productTypes[0]);
    setSelectedTypeId(busiest?.id ?? productTypes[0]?.id);
  }, [productTypes, selectedTypeId, formationsByType]);

  // Notify parent of total active count for rail
  const activeCount = useMemo(() => {
    return productFormations.filter((f) => !f.is_legacy).length;
  }, [productFormations]);

  const legacyCount = useMemo(() => {
    return productFormations.filter((f) => f.is_legacy).length;
  }, [productFormations]);

  useEffect(() => {
    if (onTotalChange) {
      onTotalChange(activeCount);
    }
  }, [activeCount, onTotalChange]);

  useEffect(() => {
    if (write?.mode === "create" || write?.mode === "rename") {
      writeInputRef.current?.focus();
      writeInputRef.current?.select();
    }
  }, [write?.mode, write?.id]);

  const isLegacyView = selectedTypeId === "legacy";
  const selectedType = productTypes.find((pt) => pt.id === selectedTypeId);
  const selectedTypeName = isLegacyView ? "Legacy (Archived)" : selectedType?.name || "";

  // Active formations for the selected type (or legacy list)
  const currentFormations = useMemo(() => {
    if (isLegacyView) {
      return productFormations.filter((f) => f.is_legacy);
    }
    return selectedTypeId ? (formationsByType[selectedTypeId] || []) : [];
  }, [isLegacyView, selectedTypeId, productFormations, formationsByType]);

  // Duplicate check within currently scoped list
  const duplicateOf = useCallback(
    (value, excludeId = null) => {
      const norm = value.trim().toLowerCase();
      if (!norm) return null;
      return currentFormations.find(
        (f) => f.name.trim().toLowerCase() === norm && f.id !== excludeId
      ) || null;
    },
    [currentFormations]
  );

  const duplicate =
    write && (write.mode === "create" || write.mode === "rename")
      ? duplicateOf(write.value, write.mode === "rename" ? write.id : null)
      : null;

  const errorText = (err, fallback) =>
    (err.response && err.response.data && err.response.data.message) ||
    err.message ||
    fallback;

  // -- write actions ------------------------------------------------------------
  const submitCreate = async () => {
    const name = (write?.value || "").trim();
    if (!name || duplicate || isLegacyView || !selectedTypeId) return;

    setSaving(true);
    setError(null);
    try {
      const res = await axiosClient.post("/settings/productFormations/store", {
        name,
        product_type_id: selectedTypeId,
      });
      toast.success(res.data?.message || `Added “${name}”`);
      if (fetchProductFormations) await fetchProductFormations();
      // Keep strip open for fast consecutive additions
      setWrite({ mode: "create", value: "" });
    } catch (err) {
      const msg = errorText(err, "Failed to create formation");
      toast.error(msg);
      setError(msg);
    } finally {
      setSaving(false);
      writeInputRef.current?.focus();
    }
  };

  const submitRename = async () => {
    const name = (write?.value || "").trim();
    const original = currentFormations.find((f) => f.id === write.id);
    if (!name || duplicate) return;
    if (name === original?.name) return closeWrite();

    setSaving(true);
    setError(null);
    try {
      const res = await axiosClient.put(`/settings/productFormations/update/${write.id}`, {
        name,
      });
      toast.success(res.data?.message || `Updated “${name}”`);
      if (fetchProductFormations) await fetchProductFormations();
      closeWrite();
    } catch (err) {
      const msg = errorText(err, "Failed to rename formation");
      toast.error(msg);
      setError(msg);
      setSaving(false);
    }
  };

  const submitDelete = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await axiosClient.delete(`/settings/productFormations/delete/${write.id}`);
      toast.success(res.data?.message || "Deleted formation");
      if (fetchProductFormations) await fetchProductFormations();
      closeWrite();
    } catch (err) {
      setError(errorText(err, "Failed to delete formation"));
      setSaving(false);
    }
  };

  const onWriteKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      write.mode === "create" ? submitCreate() : submitRename();
    } else if (e.key === "Escape") {
      e.preventDefault();
      closeWrite();
    }
  };

  // Filter by search
  const ft = search.trim().toLowerCase();
  const shown = ft
    ? currentFormations.filter((f) => f.name.toLowerCase().includes(ft))
    : currentFormations;

  const target = write?.id ? currentFormations.find((f) => f.id === write.id) : null;

  const countText = selectedTypeName
    ? `${(ft ? shown.length : currentFormations.length).toLocaleString()} in ${selectedTypeName}`
    : `${currentFormations.length}`;

  const isEmpty = !productFormationsLoading && currentFormations.length === 0 && !ft;
  const noMatch = !productFormationsLoading && shown.length === 0 && ft;

  return (
    <section className="ps-pane">
      {/* ---- header ---- */}
      <header className="ps-pane-head">
        <div className="ps-pane-title">
          <span className="ps-pane-name">Product Formations</span>
          <span className="ps-pane-count">{countText}</span>
        </div>

        <div className="ps-pane-tools">
          <div className={`ps-search ${search ? "ps-search-active" : ""}`}>
            <span className="ps-search-icon" aria-hidden="true" />
            <input
              ref={searchInputRef}
              className="ps-search-input"
              type="text"
              placeholder={selectedTypeName ? `Search in ${selectedTypeName}` : "Search formations"}
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
            />
            {!search && <span className="ps-search-kbd">/</span>}
          </div>

          {!isLegacyView && (
            <button
              type="button"
              className="ps-btn-primary"
              disabled={!selectedTypeId}
              title={selectedTypeId ? `Add a formation to ${selectedTypeName}` : "Pick a product type first"}
              onClick={() => setWrite({ mode: "create", value: "" })}
            >
              Add formation
            </button>
          )}
        </div>
      </header>

      {/* ---- scope bar: product types chips ---- */}
      <div className="ps-scope">
        <span className="ps-scope-label">Product type</span>
        <div className="ps-scope-chips">
          {productTypesLoading && productTypes.length === 0 ? (
            <span className="ps-scope-loading">Loading product types…</span>
          ) : (
            <>
              {productTypes.map((pt) => {
                const count = (formationsByType[pt.id] || []).length;
                const isSelected = pt.id === selectedTypeId;
                return (
                  <button
                    type="button"
                    key={pt.id}
                    role="radio"
                    aria-checked={isSelected}
                    className={`ps-chip ${isSelected ? "ps-chip-active" : ""}`}
                    onClick={() => {
                      setSelectedTypeId(pt.id);
                      closeWrite();
                    }}
                  >
                    {pt.name}
                    {count > 0 && <span className="ps-chip-count">{count}</span>}
                  </button>
                );
              })}

              {/* Legacy view chip */}
              {legacyCount > 0 && (
                <button
                  type="button"
                  key="legacy"
                  role="radio"
                  aria-checked={isLegacyView}
                  className={`ps-chip ${isLegacyView ? "ps-chip-active" : ""}`}
                  style={{ marginLeft: 8, opacity: isLegacyView ? 1 : 0.8 }}
                  onClick={() => {
                    setSelectedTypeId("legacy");
                    closeWrite();
                  }}
                >
                  Legacy
                  <span className="ps-chip-count" style={{ background: "rgba(239,68,68,.18)", color: "var(--et-error, #ef4444)" }}>
                    {legacyCount}
                  </span>
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* ---- docked write strip ---- */}
      {write?.mode === "create" && (
        <div className="ps-strip">
          <div className="ps-strip-label">
            New formation · {selectedTypeName}
          </div>
          <div className="ps-strip-row">
            <input
              ref={writeInputRef}
              className={`ps-strip-input ${error || duplicate ? "ps-strip-input-error" : ""}`}
              placeholder="Formation name (e.g. 2+2+1 Large)"
              value={write.value}
              onChange={(e) => {
                setWrite({ ...write, value: e.target.value });
                setError(null);
              }}
              onKeyDown={onWriteKeyDown}
            />
            <button
              type="button"
              className="ps-btn-primary ps-btn-sm"
              disabled={saving || !write.value.trim() || !!duplicate}
              onClick={submitCreate}
            >
              {saving ? "Saving" : "Save"}
              {saving ? <span className="ps-spinner" /> : <span className="ps-kbd">⏎</span>}
            </button>
            <button type="button" className="ps-btn-quiet" onClick={closeWrite}>
              Cancel
            </button>
          </div>
          {duplicate && (
            <div className="ps-strip-error">
              “{duplicate.name}” already exists in {selectedTypeName}.
            </div>
          )}
          {error && <div className="ps-strip-error">{error}</div>}
        </div>
      )}

      {write?.mode === "rename" && (
        <div className="ps-strip">
          <div className="ps-strip-label">Renaming · {target?.name}</div>
          <div className="ps-strip-row">
            <input
              ref={writeInputRef}
              className={`ps-strip-input ps-strip-input-focus ${error || duplicate ? "ps-strip-input-error" : ""}`}
              value={write.value}
              onChange={(e) => {
                setWrite({ ...write, value: e.target.value });
                setError(null);
              }}
              onKeyDown={onWriteKeyDown}
            />
            <button
              type="button"
              className="ps-btn-primary ps-btn-sm"
              disabled={saving || !write.value.trim() || !!duplicate}
              onClick={submitRename}
            >
              {saving ? "Saving" : "Save"}
              {saving ? <span className="ps-spinner" /> : <span className="ps-kbd">⏎</span>}
            </button>
            <button type="button" className="ps-btn-quiet" onClick={closeWrite}>
              Esc
            </button>
          </div>
          {duplicate && (
            <div className="ps-strip-error">
              “{duplicate.name}” already exists in {selectedTypeName}.
            </div>
          )}
          {error && <div className="ps-strip-error">{error}</div>}
        </div>
      )}

      {write?.mode === "delete" && (
        <div className="ps-strip ps-strip-danger">
          <div className="ps-strip-title">Delete “{target?.name}”?</div>
          <div className="ps-strip-body">
            It is removed from {selectedTypeName} and will no longer be offered on new orders.
            If any existing order or stock record uses it, the server will prevent deletion.
          </div>
          <div className="ps-strip-row">
            <button type="button" className="ps-btn-danger" disabled={saving} onClick={submitDelete}>
              {saving ? "Deleting…" : "Delete formation"}
            </button>
            <button type="button" className="ps-btn-quiet" onClick={closeWrite}>
              Keep it
            </button>
          </div>
          {error && (
            <div className="ps-strip-refusal">
              <span className="ps-strip-refusal-dot" aria-hidden="true" />
              <span>{error}</span>
            </div>
          )}
        </div>
      )}

      {/* ---- scroll region ---- */}
      <div className={`ps-scroll ${write ? "ps-scroll-dimmed" : ""}`}>
        {productFormationsLoading && currentFormations.length === 0 && (
          <div className="ps-skeleton" aria-hidden="true">
            {[38, 52, 29, 44, 34].map((w, i) => (
              <div className="ps-skeleton-row" key={i}>
                <span className="ps-skeleton-bar" style={{ width: `${w}%` }} />
              </div>
            ))}
          </div>
        )}

        {isLegacyView && (
          <div style={{ padding: "10px 14px", background: "var(--surface2)", borderBottom: "1px solid var(--border)", fontSize: 12.5, color: "var(--muted)" }}>
            These <strong>{legacyCount} formations</strong> are historical free-text entries. They are preserved for past order history and stock tracking, but hidden from new order creation.
          </div>
        )}

        {isEmpty && (
          <div className="ps-blank">
            <span className="ps-blank-mark" aria-hidden="true" />
            <div className="ps-blank-title">No formations in {selectedTypeName} yet</div>
            <div className="ps-blank-body">
              Formations define the cuts, shapes, and seating sets a {selectedTypeName} can be made in.
            </div>
            {!isLegacyView && (
              <button
                type="button"
                className="ps-btn-primary"
                onClick={() => setWrite({ mode: "create", value: "" })}
              >
                Add the first formation
              </button>
            )}
          </div>
        )}

        {noMatch && (
          <div className="ps-blank">
            <div className="ps-blank-title">Nothing matches “{search}”</div>
            <div className="ps-blank-body">
              {currentFormations.length} {currentFormations.length === 1 ? "formation exists" : "formations exist"} in {selectedTypeName}. Clear the search to see them all.
            </div>
            <div className="ps-blank-actions">
              <button type="button" className="ps-btn-quiet" onClick={() => onSearchChange("")}>
                Clear search
              </button>
              {!isLegacyView && (
                <button
                  type="button"
                  className="ps-btn-primary"
                  onClick={() => setWrite({ mode: "create", value: search.trim() })}
                >
                  Add “{search.trim()}”
                </button>
              )}
            </div>
          </div>
        )}

        {!isEmpty &&
          !noMatch &&
          shown.map((item) => {
            const isTarget = write?.id === item.id;
            return (
              <div
                key={item.id}
                className={`ps-row ${isTarget ? "ps-row-target" : ""}`}
              >
                <span className="ps-row-name" title={item.name}>
                  {ft ? <HighlightMatch text={item.name} query={ft} /> : item.name}
                </span>

                {item.is_legacy ? <span className="ps-tag" style={{ background: "rgba(239,68,68,.12)", color: "var(--et-error, #ef4444)" }}>LEGACY</span> : null}

                {isTarget ? (
                  <span className="ps-row-state">
                    {write.mode === "rename" ? "being renamed" : "being deleted"}
                  </span>
                ) : (
                  <>
                    <button
                      type="button"
                      className="ps-row-action"
                      onClick={() => setWrite({ mode: "rename", id: item.id, value: item.name })}
                    >
                      Rename
                    </button>
                    <button
                      type="button"
                      className="ps-row-action ps-row-action-danger"
                      onClick={() => setWrite({ mode: "delete", id: item.id })}
                    >
                      Delete
                    </button>
                  </>
                )}
              </div>
            );
          })}
      </div>
    </section>
  );
}

function HighlightMatch({ text, query }) {
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="ps-mark">{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  );
}
